/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import './BotHistoryTable.scss';
import { setBotHistorySorting } from '../../store/bot/botActions';

import {
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Collapse
} from '@mui/material';

import { 
  TrendingUp,
  TrendingDown,
  CheckCircle,
  XCircle,
  BarChart3,
  Hash,
  DollarSign,
  Target,
  Calendar,
  Filter,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const columns = [
  { id: 'expand', label: '', sortable: false, width: '50px' },
  { id: 'index', label: '#', sortable: false, icon: Hash, width: '60px' },
  { id: 'buy_order_id', label: 'Order ID', sortable: true, width: '120px' },
  { id: 'token', label: 'Token', sortable: true, width: '100px' },
  { id: 'profit_loss', label: 'P&L ($)', sortable: true, icon: DollarSign, width: '120px' },
  { id: 'profit_loss_percentage', label: 'P&L (%)', sortable: true, icon: Target, width: '100px' },
  { id: 'avg_buy_price', label: 'Avg Buy Price', sortable: true, width: '140px' },
  { id: 'avg_sell_price', label: 'Avg Sell Price', sortable: true, width: '140px' },
  { id: 'buy_timestamp', label: 'Buy Time', sortable: true, icon: Calendar, width: '160px' },
  { id: 'sales_timestamp', label: 'Sell Time', sortable: true, icon: Calendar, width: '160px' },
  { id: 'status', label: 'Status', sortable: true, width: '100px' }
];

const expandableColumns = [
  { id: 'total_buy_cost', label: 'Buy Cost', width: '120px' },
  { id: 'sales_proceeds', label: 'Sell Cost', width: '120px' },
  { id: 'total_buy_qty', label: 'Buy Qty', width: '120px' },
  { id: 'total_sales_qty', label: 'Sell Qty', width: '120px' }
];

function BotHistoryTable(props) {
  const { botHistoryData, limit, currentPages, onLimitChange,
    setBotHistorySorting,
    botHistorySorting,
  } = props;

  const [rowsPerPage, setRowsPerPage] = useState(limit || 10);
  const [page, setPage] = useState(0);
  const [localData, setLocalData] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Keep original formatting function
  // const formatValue = (value) => {
  //   return value?.toLocaleString('tr-TR');
  // };

  useEffect(() => {
    if (limit) {
      setRowsPerPage(limit);
    }
  }, [limit]);

  useEffect(() => {
    if (botHistoryData) {
      if (orderBy) {
        const sortedData = sortData([...botHistoryData], orderBy, order);
        setLocalData(sortedData);
      } else {
        setLocalData(botHistoryData);
      }
    } else {
      setLocalData([]);
    }
  }, [botHistoryData]);

  const toggleRowExpansion = (index) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(index)) {
      newExpandedRows.delete(index);
    } else {
      newExpandedRows.add(index);
    }
    setExpandedRows(newExpandedRows);
  };

  const sortData = (data, property, sortOrder) => {
    return [...data].sort((a, b) => {
      if (property === 'profit_loss' || property === 'profit_loss_percentage' ||
        property === 'avg_buy_price' || property === 'avg_sell_price' ||
        property === 'total_buy_qty' || property === 'total_buy_cost' ||
        property === 'total_sales_qty' || property === 'sales_proceeds') {
        const aValue = a[property] === null || a[property] === undefined ? 0 : Number(a[property]);
        const bValue = b[property] === null || b[property] === undefined ? 0 : Number(b[property]);
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      } else {
        const aValue = a[property] === null ||
          a[property] === undefined ? '' : a[property].toString();
        const bValue = b[property] === null ||
          b[property] === undefined ? '' : b[property].toString();
        return sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
    });
  };

  const handleRequestSort = (property) => {
    setLoading(true);

    setTimeout(() => {
      const isAsc = orderBy === property && order === 'asc';
      const newOrder = isAsc ? 'desc' : 'asc';
      setOrder(newOrder);
      setOrderBy(property);

      setBotHistorySorting(property, newOrder);

      const sortedData = sortData(localData, property, newOrder);
      setLocalData(sortedData);

      setLoading(false);
    }, 300);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);

    if (onLimitChange) {
      if (event.target.value === 'All') {
        onLimitChange(100000);
      } else {
        onLimitChange(newRowsPerPage);
      }
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const getRowsPerPageValue = (value) => {
    return value === 100000 ? 'All' : value;
  };

  const ProfitLossChip = ({ value }) => {
    const isProfit = value > 0;
    return (
      <Chip
        icon={isProfit ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
        label={'$' + value}
        color={isProfit ? "success" : "error"}
        size="small"
        variant="filled"
        className={`modern-pnl-chip ${isProfit ? "positive-chip" : "negative-chip"}`}
      />
    );
  };

  const StatusChip = ({ status }) => {
    const isOpen = status;
    return (
      <Chip
        icon={isOpen ? <CheckCircle size={14} /> : <XCircle size={14} />}
        label={isOpen ? "Open" : "Closed"}
        color={isOpen ? "success" : "error"}
        size="small"
        variant="filled"
        className="modern-status-chip"
      />
    );
  };

  return (
    <div className="modern-bot-history-table">
      <Paper elevation={0} className="modern-table-paper">
        {/* Enhanced controls with modern styling but original functionality */}
        {localData.length > 0 &&
          <Box className="modern-table-controls">
            <div className="controls-left">
              <Typography variant="body2" component="span" className="control-label">
                <Filter size={16} className="control-icon" />
                Limit:
              </Typography>
              <FormControl variant="standard" size="small" className="modern-limit-select">
                <Select
                  value={rowsPerPage === 100000 ? 'All' : rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  displayEmpty
                  className="modern-select"
                >
                  {[5, 10, 15, 20, 50, 100, 'All'].map((option) => (
                    <MenuItem key={option} value={option === 'All' ? 'All' : option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="controls-right">
              <Typography variant="body2" component="span" className="count-display">
                <BarChart3 size={16} className="control-icon" />
                Count: {botHistoryData?.length || 0}
              </Typography>
            </div>
          </Box>
        }

        <TableContainer className="modern-table-container" id="bot-history-container">
          <Table stickyHeader aria-label="bot history table" className="modern-table">
            <TableHead>
              <TableRow className="modern-header-row">
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    className="modern-header-cell"
                    sortDirection={orderBy === column.id ? order : false}
                  >
                    {column.id === 'expand' ? (
                      ''
                    ) : column.sortable ? (
                      <TableSortLabel
                        active={orderBy === column.id}
                        direction={orderBy === column.id ? order : 'asc'}
                        onClick={() => handleRequestSort(column.id)}
                        className="modern-sort-label"
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="modern-loading-message">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : localData.length > 0 ? (
                localData.map((item, index) => {
                  const rowIndex = (currentPages - 1) * rowsPerPage + index + 1;
                  const isExpanded = expandedRows.has(index);
                  
                  return (
                    <React.Fragment key={index}>
                      <TableRow
                        hover
                        className="modern-table-row"
                      >
                        <TableCell className="modern-cell">
                          <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => toggleRowExpansion(index)}
                            className="expand-button"
                          >
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </IconButton>
                        </TableCell>
                        <TableCell className="modern-cell">{rowIndex}</TableCell>
                        <TableCell className="modern-cell">{item.buy_order_id}</TableCell>
                        <TableCell className="modern-cell token-cell">{item.token}</TableCell>
                        <TableCell className="modern-cell">
                          <ProfitLossChip value={item.profit_loss} />
                        </TableCell>
                        <TableCell className="modern-cell">{item.profit_loss_percentage}%</TableCell>
                        <TableCell className="modern-cell">{'$' + (item.avg_buy_price)}</TableCell>
                        <TableCell className="modern-cell">{'$' + (item.avg_sell_price)}</TableCell>
                        <TableCell className="modern-cell">{item.buy_timestamp}</TableCell>
                        <TableCell className="modern-cell">{item.sales_timestamp}</TableCell>
                        <TableCell className="modern-cell">
                          <StatusChip status={item.status} />
                        </TableCell>
                      </TableRow>
                      
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={columns.length}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 1 }}>
                              <Typography variant="h6" gutterBottom component="div" className="detail-title">
                                Detailed Information
                              </Typography>
                              <Table size="small" aria-label="detail">
                                <TableBody>
                                  <TableRow>
                                    {expandableColumns.map((column) => (
                                      <TableCell key={column.id} className="detail-cell">
                                        <Typography variant="body2" className="detail-label">
                                          {column.label}
                                        </Typography>
                                        <Typography variant="body1" className="detail-value">
                                          {column.id.includes('cost') || column.id.includes('proceeds') 
                                            ? '$' + (item[column.id] || 0)
                                            : (item[column.id] || 0)
                                          }
                                        </Typography>
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  )
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={columns.length} className="modern-empty-message">
                    <div className="empty-state">
                      <BarChart3 size={48} className="empty-icon" />
                      <Typography variant="h6">No Bot history found.</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Bottom controls with modern styling */}
        {localData.length > 0 &&
          <Box className="modern-table-footer">
            <div className="footer-left">
              <Typography variant="body2" component="span" className="control-label">
                Limit:
              </Typography>
              <FormControl variant="standard" size="small" className="modern-limit-select">
                <Select
                  value={rowsPerPage === 100000 ? 'All' : rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  displayEmpty
                  className="modern-select"
                >
                  {[5, 10, 15, 20, 50, 100, 'All'].map((option) => (
                    <MenuItem key={option} value={option === 'All' ? 'All' : option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="footer-right">
              <Typography variant="body2" component="span" className="count-display">
                Count: {botHistoryData?.length || 0}
              </Typography>
            </div>
          </Box>
        }
      </Paper>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    botHistorySorting: state.botReducer.botHistorySorting,
  };
};
const mapDispatchToProps = {
  setBotHistorySorting
};

export default connect(mapStateToProps, mapDispatchToProps)(BotHistoryTable);